import { openDB, type IDBPDatabase } from 'idb';
import { log } from '../log/log';
// Use explicit `.ts` so Vite's platform-extension resolver does not
// collapse `'./PingStorage'` back into `PingStorage.web.ts` (this file).
import { PingStorage, StorageCorruption, type PingStorageConfig } from './PingStorage.ts';

export { StorageCorruption } from './PingStorage.ts';
export type { Storage, PingStorageConfig } from './PingStorage.ts';

const DB_NAME = 'ping-mls-storage';
const STORE_NAME = 'storage';
const DB_VERSION = 1;
const IV_LENGTH = 12;
// Persistent slot for the AES wrap key. The key is a non-extractable
// `CryptoKey` — browsers structured-clone CryptoKeys into IndexedDB
// natively, so the key handle survives reloads without ever being
// extractable to userland. Stored under a reserved object-store key
// that shares the `STORE_NAME` schema with user data; we deliberately
// pick a leading character that can't appear in a real
// `${namespace}:${key}` triple (namespaces never start with `__`).
//
// Before this slot existed the wrap key was generated fresh on every
// `ensureInitialized()` call, and the previous load's ciphertext was
// always "corrupted" against the new key — every reload tripped
// `StorageCorruption: Failed to decrypt stored value`, the bootstrap
// catch wiped local state, and the user effectively signed out on
// every page refresh. The exact same gotcha that
// `apps/web/.../identity.web.ts` already documents.
const WRAP_KEY_RECORD = '__wrap-key';

interface StoredValue {
  iv: Uint8Array;
  ciphertext: Uint8Array;
}

export class PingStorageWeb extends PingStorage {
  private db: IDBPDatabase | null = null;
  private wrapKey: CryptoKey | null = null;
  private readonly dbName: string;

  constructor(config?: PingStorageConfig) {
    super();
    this.dbName = config?.dbName ?? DB_NAME;
  }

  private async ensureInitialized(): Promise<void> {
    if (this.db && this.wrapKey) {
      return;
    }

    this.db = await openDB(this.dbName, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      },
    });

    // Load the persisted wrap key if it exists; otherwise mint a fresh
    // non-extractable one and persist it under `WRAP_KEY_RECORD`. The
    // returned handle is the same on subsequent loads, so previously-
    // encrypted ciphertext decrypts cleanly across reloads.
    const stored = await this.db.get(STORE_NAME, WRAP_KEY_RECORD);
    if (stored !== undefined && stored !== null && typeof stored === 'object' && 'algorithm' in stored) {
      this.wrapKey = stored as CryptoKey;
      log.debug('storage-mls wrapKey loaded', { dbName: this.dbName });
    } else {
      this.wrapKey = await crypto.subtle.generateKey(
        { name: 'AES-GCM', length: 256 },
        false, // non-extractable
        ['encrypt', 'decrypt'],
      );
      await this.db.put(STORE_NAME, this.wrapKey, WRAP_KEY_RECORD);
      log.debug('storage-mls wrapKey generated + persisted', { dbName: this.dbName });
    }

    log.debug('storage-mls initialized', { dbName: this.dbName });
  }

  private makeKey(namespace: string, key: string): string {
    return `${namespace}:${key}`;
  }

  private async encrypt(plaintext: Uint8Array): Promise<StoredValue> {
    if (!this.wrapKey) {
      throw new Error('Storage not initialized');
    }

    const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
    const ciphertextBuffer = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: iv as BufferSource },
      this.wrapKey,
      plaintext as BufferSource,
    );

    return {
      iv,
      ciphertext: new Uint8Array(ciphertextBuffer),
    };
  }

  private async decrypt(stored: StoredValue): Promise<Uint8Array> {
    if (!this.wrapKey) {
      throw new Error('Storage not initialized');
    }

    try {
      const plaintextBuffer = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: stored.iv as BufferSource },
        this.wrapKey,
        stored.ciphertext as BufferSource,
      );
      return new Uint8Array(plaintextBuffer);
    } catch (error) {
      throw new StorageCorruption('Failed to decrypt stored value', error as Error);
    }
  }

  async get(namespace: string, key: string): Promise<Uint8Array | null> {
    await this.ensureInitialized();
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    const compositeKey = this.makeKey(namespace, key);
    const stored = await this.db.get(STORE_NAME, compositeKey);

    if (!stored) {
      log.debug('storage.get', { namespace, hasKey: false });
      return null;
    }

    const plaintext = await this.decrypt(stored);
    log.debug('storage.get', { namespace, hasKey: true, byteCount: plaintext.length });
    return plaintext;
  }

  async put(namespace: string, key: string, value: Uint8Array): Promise<void> {
    await this.ensureInitialized();
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    const compositeKey = this.makeKey(namespace, key);
    const encrypted = await this.encrypt(value);

    await this.db.put(STORE_NAME, encrypted, compositeKey);
    log.debug('storage.put', { namespace, byteCount: value.length });
  }

  async del(namespace: string, key: string): Promise<void> {
    await this.ensureInitialized();
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    const compositeKey = this.makeKey(namespace, key);
    await this.db.delete(STORE_NAME, compositeKey);
    log.debug('storage.del', { namespace });
  }

  async listKeys(namespace: string): Promise<string[]> {
    await this.ensureInitialized();
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    const prefix = `${namespace}:`;
    const allKeys = await this.db.getAllKeys(STORE_NAME);

    const keys: string[] = [];
    for (const compositeKey of allKeys) {
      if (typeof compositeKey === 'string' && compositeKey.startsWith(prefix)) {
        keys.push(compositeKey.slice(prefix.length));
      }
    }

    log.debug('storage.listKeys', { namespace, count: keys.length });
    return keys;
  }

  async forgetSession(): Promise<void> {
    if (this.db) {
      this.db.close();
      this.db = null;
    }

    this.wrapKey = null;

    const databases = await indexedDB.databases();
    const targetDb = databases.find((db) => db.name === this.dbName);

    if (targetDb) {
      await new Promise<void>((resolve, reject) => {
        const request = indexedDB.deleteDatabase(this.dbName);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
        request.onblocked = () => {
          log.warn('storage.forgetSession blocked', { dbName: this.dbName });
        };
      });
    }

    log.info('storage.forgetSession complete', { dbName: this.dbName });
  }
}

export { PingStorageWeb as PingStorage };
